import {alpha, createTheme} from '@mui/material/styles';
import {stepLabelClasses} from "@mui/material";
import {QontoConnector, QontoStepIcon} from "./CustomComponent";

const palette = {
    mode: 'light',
    primary: {
        main: '#1D8B45',
        light: '#DFF6E6',
        dark: '#16713A',
        contrastText: '#FFFFFF',
        50: '#F0F9F4',
        100: '#DFF6E6',
        200: '#BFECCD',
        300: '#9FDEB4',
        400: '#5FCD85',
        500: '#1D8B45',
        600: '#16713A',
        700: '#11542B',
        800: '#0B381D',
        900: '#061C0E',
    },
    secondary: {
        main: '#F9B347',
        light: '#FFEAC2',
        dark: '#C97706',
        contrastText: '#FFFFFF',
        50: '#FFF7ED',
        100: '#FFEAC2',
        200: '#FDD58E',
        300: '#FCBF59',
        400: '#FAAA25',
        500: '#F9B347',
        600: '#C97706',
        700: '#915604',
        800: '#613903',
        900: '#301D01',
    },
    success: {main: '#16713A'},
    warning: {main: '#C97706'},
    info: {main: '#1E40AF'},
    error: {main: '#B91C1C'},
    background: {
        default: '#FAFAF9',
        paper: '#FFFFFF',
    },
    text: {
        primary: '#1C1917',
        secondary: '#57534E',
    },
    divider: '#E7E5E4',
    primaryScale: {
        50: '#F1FBF4',
        100: '#DFF6E6',
        200: '#BFECCF',
        300: '#95D9AD',
        400: '#59BE7A',
        500: '#1D8B45',
        600: '#16713A',
        700: '#115C30',
        800: '#0E4A28',
        900: '#0B3B20'
    },
    secondaryScale: {
        50: '#FFF6E5',
        100: '#FFEAC2',
        200: '#FFD58A',
        300: '#FFC157',
        400: '#FFB034',
        500: '#F9B347',
        600: '#E39B3B',
        700: '#C88332',
        800: '#A86E2A',
        900: '#8A5A22'
    },
    neutral: {
        50: '#FAFAF9',
        100: '#F5F5F4',
        200: '#E7E5E4',
        300: '#D6D3D1',
        400: '#A8A29E',
        500: '#78716C',
        600: '#57534E',
        700: '#44403C',
        800: '#292524',
        900: '#1C1917',
    },
    semantic: {
        success: '#16713A',
        warning: '#C97706',
        info: '#1E40AF',
        error: '#B91C1C',
        errorDark: 'rgb(129, 19, 19)'
    }
};

const typography = {
    fontFamily: 'Roboto, Inter, sans-serif',
    fontSize: 14,

    h1: {fontSize: 30, lineHeight: 1.25, fontWeight: 600, letterSpacing: '-1.2px'},
    h2: {fontSize: 24, lineHeight: 1.3, fontWeight: 600, letterSpacing: '-0.72px'},
    h3: {fontSize: 20, lineHeight: 1.35, fontWeight: 600, letterSpacing: '-0.6px'},
    h4: {fontSize: 18, lineHeight: 1.4, fontWeight: 500, letterSpacing: '-0.18px'},

    subtitle1: {fontSize: 16, lineHeight: 1.5, fontWeight: 500, letterSpacing: '0px'},

    body1: {fontSize: 16, lineHeight: 1.6, fontWeight: 400},
    body2: {fontSize: 14, lineHeight: 1.6, fontWeight: 400},
    caption: {fontSize: 13, lineHeight: 1.4, fontWeight: 400},

    button: {fontSize: 13, lineHeight: 1.4, fontWeight: 700, textTransform: 'none'},

    tableHeader: {fontSize: 14, lineHeight: 1.4, fontWeight: 600, letterSpacing: '1.96px', textTransform: 'none'},
    statusPill: {
        fontSize: '0.75rem',
        lineHeight: 1.2,
        fontWeight: 700,
        letterSpacing: '0.72px',
        textTransform: 'uppercase'
    },
    mono: {
        fontSize: 14,
        lineHeight: 1.5,
        fontWeight: 400,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
    },
};

const customShadows = Array(25).fill('none');
customShadows[1] = '0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 1px rgba(15, 23, 42, 0.04)';
customShadows[3] = '0 4px 10px rgba(15, 23, 42, 0.1)';
customShadows[5] = '0 12px 24px rgba(15, 23, 42, 0.14)';

const shape = {
    borderRadius: 4,
    sm: 2,
    md: 4,
    lg: 8,
    xl: 999
};

const buttonSizes = {
    small: {fontSize: 12, minHeight: 32, padding: '6px 12px'},
    medium: {fontSize: 13, minHeight: 36, padding: '8px 16px'},
    large: {fontSize: 14, minHeight: 40, padding: '10px 20px'}
};

const buttonConfig = () => ({
    defaultProps: {
        disableElevation: true,
    },
    styleOverrides: {
        root: ({theme}) => ({
            textTransform: theme.typography.button.textTransform,
            fontWeight: theme.typography.button.fontWeight,
            fontSize: theme.typography.button.fontSize,
            borderRadius: theme.shape.md,
            padding: theme.spacing(1, 2),
            minHeight: 36
        }),

        sizeSmall: {...buttonSizes.small},
        sizeMedium: {...buttonSizes.medium},
        sizeLarge: {...buttonSizes.large},

        containedPrimary: ({theme}) => ({
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark
            },
        }),
        outlinedPrimary: ({theme}) => ({
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
        }),
        textPrimary: ({theme}) => ({
            '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
            },
        }),
        containedError: ({theme}) => ({
            '&:hover': {
                backgroundColor: theme.palette.error.dark,
            },
        }),
        outlinedError: ({theme}) => ({
            '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
        }),
    },
    variants: [
        {
            props: {variant: 'whiteRoundOutlined'},
            style: ({theme}) => ({
                border: `1px solid ${theme.palette.neutral[300]}`,
                borderRadius: theme.shape.xl,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:hover": {
                    border: `1px solid ${theme.palette.neutral[300]}`,
                    filter: 'brightness(0.9)',
                    backgroundColor: theme.palette.background.paper,
                }
            }),
        },
        {
            props: {variant: 'whiteRegularOutlined'},
            style: ({theme}) => ({
                border: `1px solid ${theme.palette.neutral[300]}`,
                borderRadius: theme.shape.lg,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:hover": {
                    border: `1px solid ${theme.palette.neutral[300]}`,
                    filter: 'brightness(0.9)',
                    backgroundColor: theme.palette.background.paper,
                }
            }),
        },
        {
            props: {variant: 'greenRegularOutlined'},
            style: ({theme}) => ({
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: theme.shape.lg,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:hover": {
                    border: `1px solid ${theme.palette.primary.main}`,
                    filter: 'brightness(0.9)',
                    backgroundColor: theme.palette.background.paper,
                }
            }),
        },
        {
            props: {variant: 'greenRegularOutlined2'},
            style: ({theme}) => ({
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: theme.shape.lg,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:hover": {
                    border: `1px solid ${theme.palette.primary.main}`,
                    filter: 'brightness(0.9)',
                    backgroundColor: theme.palette.background.paper,
                }
            }),
        },
    ],
});

const containerConfig = () => ({
    defaultProps: {
        maxWidth: 'xl',
        disableGutters: true,
    },
    styleOverrides: {
        root: ({ownerState, theme}) => ({
            boxSizing: 'border-box',

            ...(ownerState.maxWidth === 'xl' && {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                backgroundColor: 'transparent',
            }),

            ...(ownerState.maxWidth === 'xs' && {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
                [theme.breakpoints.down('sm')]: {
                    paddingLeft: theme.spacing(1),
                }
            }),

            ...(!ownerState.maxWidth && {
                margin: '0 auto',
            })
        }),
    }
});

const boxConfig = () => ({
    styleOverrides: {
        root: {
            boxSizing: 'border-box',
        },
    }
})

const typographyConfig = () => ({
    styleOverrides: {
        root: ({theme, ownerState}) => ({
            ...(ownerState.variant === 'tableHeader' && theme.typography.tableHeader),
            ...(ownerState.variant === 'statusPill' && theme.typography.statusPill),
            ...(ownerState.variant === 'mono' && theme.typography.mono),
        }),
    },
});

const linkConfig = () => ({
    styleOverrides: {
        root: ({theme}) => ({
            transition: 'all 0.2s ease-in-out',
        }),
    },
    variants: [
        {
            props: {variant: 'footer'},
            style: ({theme}) => ({
                textDecoration: 'none',
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                '&:hover': {
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                },
            }),
        },
        {
            props: {variant: 'nav'},
            style: ({theme}) => ({
                textDecoration: 'none',
                color: theme.palette.text.primary,
                fontWeight: theme.typography.subtitle1.fontWeight,
                '&:hover': {
                    textDecoration: 'underline',
                },
            }),
        },
    ],
});

const menuConfig = () => ({
    defaultProps: {
        elevation: 0,
        anchorOrigin: {vertical: 'bottom', horizontal: 'right'},
        transformOrigin: {vertical: 'top', horizontal: 'right'},
    },
    styleOverrides: {
        paper: ({theme}) => ({
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            borderRadius: theme.shape.md,
        }),
        // 在 root 级别捕获 variant 状态
        root: ({theme, ownerState}) => ({
            ...(ownerState.variant === 'compact' && {
                '& .MuiPaper-root': {
                    width: 260,
                    borderRadius: theme.shape.lg
                },
                '& .MuiMenu-list': {
                    padding: theme.spacing(1, 0, 0)
                },
            }),
        }),
    },
});

const menuItemConfig = () => ({
    styleOverrides: {
        root: ({theme, ownerState}) => ({
            padding: theme.spacing(1, 2.5),
            '& .MuiSvgIcon-root': {fontSize: 16},

            '&:hover': {
                backgroundColor: theme.custom.hover.subtle,
            },
            '&.Mui-selected': {
                backgroundColor: theme.custom.hover.strong,
                '&:hover': {
                    backgroundColor: theme.custom.hover.strong,
                },
            },

            ...(ownerState.variant === 'profile' && {
                whiteSpace: 'normal',
                width: 260,
                padding: theme.spacing(1.5, 2),
                cursor: 'default',
            }),
            ...(ownerState.variant === 'button' && {
                width: 260,
                padding: theme.spacing(1.25, 2),
                cursor: 'pointer',
            }),
            ...(ownerState.variant === 'divider' && {
                width: 260,
                padding: 0,
                cursor: 'default',
                minHeight: 'auto'
            }),
            ...(ownerState.variant === 'dense' && {
                padding: theme.spacing(0.5, 1.5),
                fontSize: theme.typography.caption.fontSize,
            }),
        }),
    },
});

const listConfig = () => ({
    variants: [
        {
            props: {variant: 'sidebar'},
            style: ({theme}) => ({padding: theme.spacing(0, 1), flexGrow: 1})
        },
        {
            props: {variant: 'sidebar-bottom'},
            style: ({theme}) => ({padding: theme.spacing(0, 1, 2), marginTop: 'auto'})
        },
    ],
});

const listItemConfig = () => ({
    variants: [
        {
            props: {variant: 'sidebar'},
            style: ({theme}) => ({
                display: 'block',
                marginBottom: theme.spacing(1),
                padding: 0,
                listStyle: 'none',
            }),
        },
    ],
});

const listItemButtonConfig = () => ({
    styleOverrides: {
        root: ({theme}) => ({
            minHeight: 36,
            borderRadius: theme.shape.lg,
            padding: theme.spacing(0.5, 2.5),
            transition: theme.transitions.create(['background-color', 'color'], {
                duration: theme.transitions.duration.shortest,
            }),
        })
    },
    variants: [
        {
            props: {variant: 'sidebar'},
            style: ({theme, ownerState}) => ({
                minHeight: 36,
                borderRadius: theme.shape.lg,
                padding: theme.spacing(0.5, 2.5),
                backgroundColor: ownerState.selected ? 'rgba(29, 139, 69, 0.1)' : 'transparent',
                '&:hover': {backgroundColor: 'rgba(29, 139, 69, 0.05)'},
            })
        },
        {
            props: {variant: 'sidebarStandard'},
            style: ({theme}) => ({
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
                '&:hover': {
                    backgroundColor: theme.custom.hover.subtle,
                },
                '&.Mui-selected': {
                    backgroundColor: theme.custom.hover.strong,
                    color: theme.palette.primary.main,
                    '&:hover': {
                        backgroundColor: theme.custom.hover.strong,
                    },
                }
            })
        },
        {
            props: {variant: 'sidebarBrand'},
            style: ({theme}) => ({
                backgroundColor: 'transparent',
                color: '#FFFFFF',
                '& .MuiListItemIcon-root svg': {color: '#FFFFFF'},
                '& .MuiListItemText-primary': {color: '#FFFFFF'},

                '&:hover': {
                    backgroundColor: alpha('#FFFFFF', 0.12), // rgba(255, 255, 255, 0.12)
                },
                '&.Mui-selected': {
                    backgroundColor: alpha('#FFFFFF', 0.18), // rgba(255, 255, 255, 0.18)
                    '&:hover': {
                        backgroundColor: alpha('#FFFFFF', 0.18),
                    },
                }
            })
        }
    ]
});

const listItemIconConfig = () => ({
    variants: [{
        props: {variant: 'sidebar'},
        style: ({theme, ownerState}) => ({
            minWidth: 0,
            justifyContent: 'center',
            marginRight: ownerState.open ? theme.spacing(2) : 0,
            '& svg': {width: 20, height: 20, color: theme.palette.background.paper}
        })
    }]
});

const listItemTextConfig = () => ({
    variants: [{
        props: {variant: 'sidebar'},
        style: ({theme, ownerState}) => ({
            margin: 0,
            display: ownerState.open ? 'block' : 'none',
            opacity: ownerState.open ? 1 : 0,
            '& .MuiListItemText-primary': {
                fontSize: theme.typography.body2.fontSize,
                fontWeight: ownerState.selected ? 600 : 500,
                color: theme.palette.background.paper,
            }
        })
    }]
});

const tooltipConfig = () => ({
    defaultProps: {
        arrow: true,
        enterTouchDelay: 0,
        leaveTouchDelay: 1500,
    },
    styleOverrides: {
        tooltip: ({theme}) => ({
            backgroundColor: theme.palette.neutral[800],
            color: theme.palette.background.paper,
            borderRadius: theme.shape.xl,

            // 文本配置：直接映射 body1
            fontSize: theme.typography.body1.fontSize,
            fontWeight: theme.typography.body1.fontWeight,
            lineHeight: 1.5,

            // 尺寸与间距
            maxWidth: 520,
            // 10.5px 是特殊值，建议使用 theme.spacing 计算以保持数学比例
            // 如果 spacing 是 8，1.3125 * 8 = 10.5
            padding: theme.spacing(1.3125, 1.75),

            // 增加阴影增强浮动感
            boxShadow: theme.shadows[8],
        }),
        arrow: ({theme}) => ({
            // 映射：确保箭头颜色与 tooltip 背景一致
            color: theme.palette.grey[800],
            // 映射：fontSize 14px 决定了箭头的大小
            fontSize: 14,
        }),
    },
});

const chipConfig = () => ({
    styleOverrides: {
        root: ({theme}) => ({
            borderRadius: theme.shape.xl,
            textTransform: 'uppercase',
        }),
    },
    variants: [
        {
            props: {variant: 'quoteAvailable'},
            style: ({theme}) => ({
                color: theme.palette.success.main,
                backgroundColor: alpha(theme.palette.success.main, 0.12),
            }),
        },
        {
            props: {variant: 'quotePending'},
            style: ({theme}) => ({
                color: theme.palette.warning.main,
                backgroundColor: alpha(theme.palette.warning.main, 0.12),
            }),
        },
        {
            props: {variant: 'quoteExpired'},
            style: ({theme}) => ({
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.12),
            }),
        },
        {
            props: {variant: 'booked'},
            style: ({theme}) => ({
                color: theme.palette.info.main,
                backgroundColor: alpha(theme.palette.info.main, 0.12),
            }),
        },
        {
            props: {variant: 'inactive'},
            style: ({theme}) => ({
                color: theme.palette.neutral?.[600],
                backgroundColor: alpha(theme.palette.neutral?.[600], 0.12),
            }),
        },
    ],
});

const HOVER_RULES = {
    subtle: '#F5F5F4',
    strong: 'rgba(29, 139, 69, 0.18)',
    outlined: 'rgba(29, 139, 69, 0.12)',
    textBg: 'transparent',
    textUnderline: 'none',
};

const stepConnectorConfig = () => ({
    styleOverrides: {
        alternativeLabel: {
            top: 11,
            left: 'calc(-50% + 20px)',
            right: 'calc(50% + 20px)',
        },
        line: ({theme}) => ({
            borderColor: theme.palette.neutral[200],
            borderTopWidth: 1,
            borderRadius: 1,
        }),
    },
});

const stepLabelConfig = () => ({
    defaultProps: {
        StepIconComponent: QontoStepIcon,
    },
    styleOverrides: {
        label: ({theme}) => ({
            marginTop: '12px !important',
            fontSize: theme.typography.body2.fontSize,
            fontWeight: 500,
            color: theme.palette.neutral[400],
            [`&.${stepLabelClasses.active}`]: {
                color: theme.palette.neutral[900],
                fontWeight: 700,
            },
            [`&.${stepLabelClasses.completed}`]: {
                color: theme.palette.neutral[900],
            },
        }),
    },
});

const stepperConfig = () => ({
    defaultProps: {
        alternativeLabel: true,
        connector: <QontoConnector/>,
    },
});

const UUCargoTheme = createTheme({
    palette: {
        ...palette,
        action: {
            hover: HOVER_RULES.subtle,
            selected: HOVER_RULES.strong,
        }
    },
    typography: typography,
    shadows: customShadows,
    spacing: 8,
    shape: shape,
    custom: {
        hover: HOVER_RULES,
    },
    components: {
        MuiContainer: containerConfig(),
        MuiBox: boxConfig(),
        MuiButton: buttonConfig(),
        MuiTypography: typographyConfig(),
        MuiLink: linkConfig(),
        MuiMenu: menuConfig(),
        MuiMenuItem: menuItemConfig(),
        MuiMenuList: {
            defaultProps: {
                variant: 'selectedMenu',
            },
        },
        MuiList: listConfig(),
        MuiListItem: listItemConfig(),
        MuiListItemButton: listItemButtonConfig(),
        MuiListItemIcon: listItemIconConfig(),
        MuiListItemText: listItemTextConfig(),
        MuiChip: chipConfig(),

        MuiStepper: stepperConfig(),
        MuiStepConnector: stepConnectorConfig(),
        MuiStepLabel: stepLabelConfig(),

        MuiStepIcon: {
            styleOverrides: {
                root: {display: 'none'}
            }
        }
    },
});

export default UUCargoTheme;